import { useEffect, useState } from 'react';

/**
 * credify-platform#306 (operator ruling 2026-10-29) - the practice admin's first EHR sign-in goes to
 * Onboarding Part Two (EHR Setup, on signup.credifyfast.com) until Part Two is submitted.
 *
 * Signup holds the answer: GET https://signup.credifyfast.com/api/onboarding/ehr-config/status with the
 * shared sign-in cookie (the Messaging unread-count pattern; no secret in the EHR). It says
 * { pending: true, startUrl, session } only to the admin of a practice that came through signup and has not
 * submitted Part Two; everyone else gets { pending: false }. `session` names this sign-in (it changes at the
 * next sign-in).
 *
 * How "later" works, so nobody is trapped:
 * - Redirect once per sign-in. The session is remembered here BEFORE leaving, so coming back (I'll Set Up
 *   Later, then Go to Your Custom EHR by Credify, or the browser's Back) never redirects again.
 * - After that the Dashboard shows a "Finish your EHR setup" banner with Continue setup, until Part Two is
 *   submitted. Its dismiss hides it until the next sign-in.
 * - Only admins, and never while viewing as someone else. Signup unreachable, slow, or answering anything
 *   unexpected: the EHR just loads (fail open).
 */
export const SIGNUP_ORIGIN = 'https://signup.credifyfast.com';
export const P2_STATUS_URL = `${SIGNUP_ORIGIN}/api/onboarding/ehr-config/status`;
export const P2_START_URL = `${SIGNUP_ORIGIN}/api/onboarding/ehr-config/start`;
const TIMEOUT_MS = 4000;
const REDIRECTED_KEY = 'ehr.p2setup.redirectedFor';
const DISMISSED_KEY = 'ehr.p2setup.bannerDismissedFor';

export interface PartTwoStatus {
  pending: boolean;
  /** Where Continue setup goes (always signup's own start route). */
  startUrl: string;
  /** This sign-in, as signup names it ('' when unknown). */
  session: string;
}

/** Who is asked at all: a signed-in admin, not while viewing as someone else. */
export function shouldCheck(
  user: { role?: string | null } | null | undefined,
  impersonating: boolean,
): boolean {
  return Boolean(user) && user?.role === 'admin' && !impersonating;
}

/** Signup's answer → a status, or null for anything unexpected (fail open). */
export function parseStatus(j: unknown): PartTwoStatus | null {
  if (!j || typeof j !== 'object') return null;
  const o = j as { pending?: unknown; startUrl?: unknown; session?: unknown };
  if (o.pending === false) return { pending: false, startUrl: P2_START_URL, session: '' };
  if (o.pending !== true) return null;
  // only ever leave for signup's own start route, whatever the answer says
  const startUrl = o.startUrl === P2_START_URL ? o.startUrl : P2_START_URL;
  const session =
    typeof o.session === 'string' && /^[A-Za-z0-9_-]{1,64}$/.test(o.session) ? o.session : '';
  return { pending: true, startUrl, session };
}

export async function fetchPartTwoStatus(
  fetchImpl: typeof fetch = fetch,
  timeoutMs = TIMEOUT_MS,
): Promise<PartTwoStatus | null> {
  const ctl = typeof AbortController === 'function' ? new AbortController() : null;
  const timer = setTimeout(() => ctl?.abort(), timeoutMs);
  try {
    const r = await fetchImpl(P2_STATUS_URL, {
      credentials: 'include',
      cache: 'no-store',
      signal: ctl?.signal,
    });
    if (!r.ok) return null;
    return parseStatus(await r.json());
  } catch {
    return null;
  } finally {
    clearTimeout(timer);
  }
}

/** The small per-browser memory (localStorage; a blocked store just means "not remembered"). */
export interface Memory {
  get(key: string): string | null;
  set(key: string, value: string): void;
}
export const browserMemory: Memory = {
  get: (k) => {
    try {
      return localStorage.getItem(k);
    } catch {
      return null;
    }
  },
  set: (k, v) => {
    try {
      localStorage.setItem(k, v);
    } catch {
      /* not remembered */
    }
  },
};

/**
 * What to do with an answer: leave for Part Two (first time this sign-in), show the banner, or nothing.
 * Without a session name a redirect could repeat on every load, so it is the banner only.
 */
export function decide(status: PartTwoStatus | null, mem: Memory): 'redirect' | 'banner' | 'none' {
  if (!status || !status.pending) return 'none';
  if (status.session && mem.get(REDIRECTED_KEY) !== status.session) return 'redirect';
  if (status.session && mem.get(DISMISSED_KEY) === status.session) return 'none';
  return 'banner';
}

/** Remember the redirect for this sign-in (call before leaving). */
export function markRedirected(status: PartTwoStatus, mem: Memory): void {
  if (status.session) mem.set(REDIRECTED_KEY, status.session);
}

/** Hide the banner until the next sign-in. */
export function markDismissed(status: PartTwoStatus, mem: Memory): void {
  mem.set(DISMISSED_KEY, status.session || 'none');
}

// One read per page load and person, shared by the shell (redirect) and the Dashboard (banner).
let cached: { key: string; promise: Promise<PartTwoStatus | null> } | null = null;
export function partTwoStatusOnce(
  key: string,
  fetchImpl: typeof fetch = fetch,
): Promise<PartTwoStatus | null> {
  if (!cached || cached.key !== key) cached = { key, promise: fetchPartTwoStatus(fetchImpl) };
  return cached.promise;
}
/** Tests only. */
export function resetPartTwoCache(): void {
  cached = null;
}

/**
 * The shell's hook: on load, for an admin, read the status once; the first time this sign-in, go to Part
 * Two. Returns what the Dashboard banner needs.
 */
export function usePartTwoSetup(
  user: { id: string; role?: string | null } | null,
  impersonating: boolean,
  opts: { redirect?: boolean; navigate?: (url: string) => void } = {},
): { status: PartTwoStatus | null; showBanner: boolean; dismiss: () => void } {
  const [status, setStatus] = useState<PartTwoStatus | null>(null);
  const [view, setView] = useState<'redirect' | 'banner' | 'none'>('none');
  const check = shouldCheck(user, impersonating);
  const userId = user?.id ?? '';
  const redirect = opts.redirect ?? false;
  const navigate = opts.navigate;
  useEffect(() => {
    if (!check) {
      setStatus(null);
      setView('none');
      return;
    }
    let live = true;
    void partTwoStatusOnce(userId).then((s) => {
      if (!live) return;
      setStatus(s);
      const v = decide(s, browserMemory);
      if (v === 'redirect' && redirect && s) {
        markRedirected(s, browserMemory);
        (navigate ?? ((url: string) => window.location.assign(url)))(s.startUrl);
        return;
      }
      // a page that does not redirect (the Dashboard) shows the banner instead
      setView(v === 'redirect' ? 'banner' : v);
    });
    return () => {
      live = false;
    };
  }, [check, userId, redirect, navigate]);
  return {
    status,
    showBanner: view === 'banner',
    dismiss: () => {
      if (status) markDismissed(status, browserMemory);
      setView('none');
    },
  };
}
